/**
 * @file The staging list of a multi-part Signify fetch and every mutation on it. Consumer:
 * `useSignifyFetchJobEntry`, which hands the proxy down to the table and its cells.
 * The proxy is per-mount component state, created once through `useRef` — no module proxy, no
 * Firestore binding, no `subscribe` — so pressing Start and unmounting the entry step disposes it.
 * Invariants: no two staged inputs share a dedup key (`keyOwners` is the index that enforces it);
 * `keys` holds operator order; an add that would exceed `SIGNIFY_FETCH_JOB_MAX_INPUTS` is refused
 * whole. Write order is load-bearing: `byKey` and `keyOwners` are written before `keys` grows and
 * cleaned after `keys` shrinks, so no cell ever renders against a row that is not there.
 * Row keys come from `uuidv4`, not `crypto.randomUUID`, which is undefined on the insecure origin a
 * developer reaching Vite over a LAN address is on.
 */

import { useCallback, useContext, useRef } from 'react';
import { proxy } from 'valtio';
import { v4 as uuidv4 } from 'uuid';
import { MessageContext } from '@/providers/MessageContext';
import type {
  SignifyFetchJobAddPlan,
  SignifyFetchJobDraft,
  SignifyFetchJobDraftEntry,
} from '../index.types';
import { planSignifyFetchJobAdditions, signifyFetchJobInputKeys } from './services';

export const useSignifyFetchJobDraft = () => {
  const draftRef = useRef<SignifyFetchJobDraft | undefined>(undefined);
  if (!draftRef.current) {
    draftRef.current = proxy<SignifyFetchJobDraft>({ keys: [], byKey: {}, keyOwners: {} });
  }
  const draft = draftRef.current;
  const { messageApi } = useContext(MessageContext);

  const addEntries = useCallback(
    (entries: readonly SignifyFetchJobDraftEntry[]): SignifyFetchJobAddPlan => {
      const plan = planSignifyFetchJobAdditions({
        entries,
        keyOwners: draft.keyOwners,
        currentCount: draft.keys.length,
      });
      if (plan.exceedsCap) return plan;

      plan.accepted.forEach(({ keys, ...entry }) => {
        const key = uuidv4();
        draft.byKey[key] = { ...entry, key };
        keys.forEach((dedupKey) => {
          draft.keyOwners[dedupKey] = key;
        });
        draft.keys.push(key);
      });
      return plan;
    },
    [draft],
  );

  /**
   * Commits an edit to a pasted row. The text is already on the proxy — the input writes it on every
   * keystroke to keep the caret — so this only re-checks the invariant, restoring `previousInput`
   * and saying why when the edit empties the row or collides with another staged input.
   */
  const commitInputText = useCallback(
    (key: string, previousInput: string): void => {
      const row = draft.byKey[key];
      if (!row) return;

      const next = row.input.trim();
      const nextKeys = signifyFetchJobInputKeys({ input: next, product: row.product });
      const collides = nextKeys.some((dedupKey) => (draft.keyOwners[dedupKey] ?? key) !== key);

      if (nextKeys.length === 0 || collides) {
        row.input = previousInput;
        messageApi.warning(
          nextKeys.length === 0
            ? 'A part cannot be empty. The previous code was restored.'
            : `"${next}" is already in the list. The previous code was restored.`,
        );
        return;
      }

      signifyFetchJobInputKeys({ input: previousInput, product: row.product }).forEach((dedupKey) => {
        if (draft.keyOwners[dedupKey] === key) delete draft.keyOwners[dedupKey];
      });
      nextKeys.forEach((dedupKey) => {
        draft.keyOwners[dedupKey] = key;
      });
      row.input = next;
    },
    [draft, messageApi],
  );

  const removeInput = useCallback(
    (key: string): void => {
      const row = draft.byKey[key];
      if (!row) return;

      const dedupKeys = signifyFetchJobInputKeys(row);
      const index = draft.keys.indexOf(key);
      if (index >= 0) draft.keys.splice(index, 1);
      dedupKeys.forEach((dedupKey) => {
        if (draft.keyOwners[dedupKey] === key) delete draft.keyOwners[dedupKey];
      });
      delete draft.byKey[key];
    },
    [draft],
  );

  const clearInputs = useCallback((): void => {
    draft.keys.splice(0, draft.keys.length);
    Object.keys(draft.byKey).forEach((key) => delete draft.byKey[key]);
    Object.keys(draft.keyOwners).forEach((dedupKey) => delete draft.keyOwners[dedupKey]);
  }, [draft]);

  return { draft, addEntries, commitInputText, removeInput, clearInputs };
};
